import * as DialogPrimitive from "@radix-ui/react-dialog";

import { Button } from "@/newComponents/Button/Button";
import { cn } from "@/shadcn/lib/utils";
import { Sheet, SheetContent } from "@/shadcn/ui/sheet";

export type DrawerSheetProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Optional while `isTitlePending`: a server-resolved title has no honest string to show yet. */
  readonly title?: string;
  readonly subtitle?: string;
  /** Shows a placeholder bar in the title's slot until the title resolves. */
  readonly isTitlePending?: boolean;
  readonly children: React.ReactNode;
  /** Caller-owned content pinned above the footer buttons (or alone, when there are none). */
  readonly footerContent?: React.ReactNode;
  readonly onCancel?: () => void;
  readonly onConfirm?: () => void | Promise<void>;
  readonly cancelText?: string;
  readonly confirmText?: string;
  readonly confirmFormId?: string;
  readonly side?: "left" | "right";
  readonly buttonsDisabled?: boolean;
};

export function DrawerSheet({
  open,
  onClose,
  title,
  subtitle,
  isTitlePending = false,
  children,
  footerContent,
  onCancel,
  onConfirm,
  cancelText = "Cancel",
  confirmText = "Confirm",
  confirmFormId,
  side = "right",
  buttonsDisabled = false,
}: DrawerSheetProps) {
  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        className={cn("flex w-full flex-col p-0 sm:max-w-[640px]")}
        side={side}
        // The subtitle is the description; without one there is none to point at.
        {...(subtitle ? {} : { "aria-describedby": undefined })}
      >
        <div className="flex flex-col gap-0.5 border-b bg-neutral-100 px-6 py-4">
          {title && (
            <DialogPrimitive.Title asChild>
              <h2 className="type-heading-md text-foreground">{title}</h2>
            </DialogPrimitive.Title>
          )}
          {!title && isTitlePending && (
            <div aria-hidden className="h-7 w-64 animate-pulse rounded bg-gray-200" />
          )}
          {subtitle && (
            <DialogPrimitive.Description asChild>
              <p className="type-body-sm text-muted-foreground italic">{subtitle}</p>
            </DialogPrimitive.Description>
          )}
        </div>

        <div className="flex-1 overflow-y-auto bg-neutral-50 px-6 py-4">{children}</div>

        {footerContent !== undefined && <div className="border-t px-6 py-4">{footerContent}</div>}

        {(onCancel || onConfirm || confirmFormId) && (
          <div className="flex items-center justify-end gap-3 border-t px-6 py-4">
            {onCancel && (
              <Button
                disabled={buttonsDisabled}
                size="md"
                type="button"
                variant="secondary"
                onClick={onCancel}
              >
                {cancelText}
              </Button>
            )}
            {(onConfirm || confirmFormId) && (
              <Button
                form={confirmFormId}
                loading={buttonsDisabled}
                size="md"
                type={confirmFormId ? "submit" : "button"}
                variant="primary"
                onClick={onConfirm}
              >
                {confirmText}
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
